import ImageWIthSignedUrl from "components/S3Utility/ImageWIthSignedUrl";
import Spinner from "components/Spinner";
import { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import authHeader from "services/auth-header";
import API from "services/axios";

/**
 * Tracking Info's Details button (user, 2026-10-19): the courier's full
 * tracking answer, laid out like Manufacturing -> DHL Tracking's Tracking
 * Details -- for DHL AND UPS.
 *
 * GET /ups-tracking/:id/details returns one shape for both couriers (nova-api
 * services/trackingDetails.js): DHL fills Shipper / Receiver with code, place
 * and facility; UPS fills From / To with the place, plus who received it.
 * Read-only: nothing is saved.
 */

// "2026-09-18", "16:39" -> "2026-09-18 - 4:39 pm" (as on the DHL modal)
export const formatEventTime = (date, time) => {
  if (!date) return "-";
  if (!time) return date;
  const [h, m] = time.split(":").map(Number);
  const h12 = h % 12 || 12;
  return `${date} - ${h12}:${String(m).padStart(2, "0")} ${h < 12 ? "am" : "pm"}`;
};

const Row = ({ label, children }) => (
  <tr>
    <td className="fw-bold pe-3 py-0 text-nowrap align-top">{label}</td>
    <td className="pe-2 py-0 align-top">:</td>
    <td className="py-0">{children || "-"}</td>
  </tr>
);

const PlaceCard = ({ side }) => (
  <div className="bg-light rounded px-2 pb-2 pt-1 h-100">
    <h4 className="fw-bold text-mute-2 mb-2">{side?.title || "-"}:</h4>
    <table className="mb-0">
      <tbody>
        {side?.code !== null && side?.code !== undefined && <Row label="Code">{side.code}</Row>}
        <Row label={side?.title === "From" || side?.title === "To" ? "Place" : "Description"}>
          {side?.place}
        </Row>
        {side?.facility && <Row label="Facility Code">{side.facility}</Row>}
      </tbody>
    </table>
  </div>
);

export default function TrackingDetailsModal({ item, onHide, formatShipDate, customerLabel }) {
  const [details, setDetails] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!item?.id) return;
    let live = true;
    setDetails(null);
    setError("");
    setLoading(true);
    API.get(`/ups-tracking/${item.id}/details`, { headers: authHeader() })
      .then(({ data }) => {
        if (!live) return;
        if (data?.success) setDetails(data.data);
        else setError(data?.message || "Could not load tracking details");
      })
      .catch((err) => live && setError(err?.response?.data?.message || err.message))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [item?.id]);

  return (
    <Modal size="lg" show={!!item} onHide={onHide}>
      <Modal.Body className="bg-white px-4" style={{ borderRadius: 15 }}>
        {/* The courier's logo at the top right (user, 2026-10-19); its name
            when it has no logo. */}
        <div className="d-flex align-items-center justify-content-between mb-3 border-bottom pb-2">
          <h4 className="mb-0">Tracking Details</h4>
          {item?.courier?.logo ? (
            <div data-testid="courier-logo" style={{ height: 40 }}>
              <ImageWIthSignedUrl
                path={item.courier.logo}
                alt={`${item.courier.name || "Courier"} logo`}
                imgStyle={{ maxHeight: 40, maxWidth: 140, width: "auto", objectFit: "contain" }}
              />
            </div>
          ) : (
            item?.courier?.name && <span className="fw-bold text-muted">{item.courier.name}</span>
          )}
        </div>
        {loading && <Spinner />}
        {error && (
          <div className="alert alert-danger py-2" role="alert">
            {error}
          </div>
        )}

        {/* item goes null the moment Close is pressed, while the modal is
            still animating out -- render nothing from it then. */}
        {details && item && (
          <>
            <table className="mb-0">
              <tbody>
                <Row label="Ship Date">{formatShipDate ? formatShipDate(item) : null}</Row>
                <Row label="Tracking No">{item?.trackingNumber}</Row>
                <Row label="Tracking Info">
                  {details.events?.[0]
                    ? [details.events[0].description, details.events[0].location]
                        .filter(Boolean)
                        .join(" - ")
                    : item?.trackingInfo}
                </Row>
                <Row label="Customer">
                  {item?.customer && customerLabel ? customerLabel(item.customer) : null}
                </Row>
                <Row label="Brand">{item?.location?.locationName}</Row>
                {details.service && <Row label="Service">{details.service}</Row>}
                {details.description && <Row label="Description">{details.description}</Row>}
                <Row label="Total Weight">{details.weight}</Row>
                <Row label={details.provider === "dhl" ? "Boxes" : "Packages"}>
                  {details.pieces}
                </Row>
                {details.deliveredTo && <Row label="Delivered To">{details.deliveredTo}</Row>}
              </tbody>
            </table>

            <div className="row my-2 g-2">
              <div className="col-6">
                <PlaceCard side={details.from} />
              </div>
              <div className="col-6">
                <PlaceCard side={details.to} />
              </div>
            </div>

            <h4 className="fw-bold my-3">Tracking History:</h4>
            <div
              style={{ maxHeight: 300, overflowY: "auto" }}
              className="p-2 bg-light shadow-sm rounded"
              data-testid="tracking-history"
            >
              {(details.events || []).map((event, index) => (
                <div key={index} className="bg-white bg-opacity-75 border rounded p-2 mb-2">
                  <table className="mb-0">
                    <tbody>
                      <Row label="Date & Time">{formatEventTime(event.date, event.time)}</Row>
                      <Row label="Description">{event.description}</Row>
                      <Row label="Location">{event.location}</Row>
                    </tbody>
                  </table>
                </div>
              ))}
              {!details.events?.length && (
                <p className="text-muted text-center mb-0">No tracking events.</p>
              )}
            </div>
          </>
        )}

        <div className="d-flex justify-content-end border-top pt-3 mt-3">
          <button type="button" className="btn btn-secondary" onClick={onHide}>
            Close
          </button>
        </div>
      </Modal.Body>
    </Modal>
  );
}
